import { ProfTag } from '@/models/profTag.ts'
import { HeyDrive, OpenedImageCaption } from '@/models/drive.ts'
import { DownloadOptions } from '@/models/downloadOptions.ts'
import { ProfProject } from '@/models/profProject.ts'
import { LayoutMode } from '@/components/Files/FilesListing.tsx'

export type FilesOrdering = 'created_at' | '-created_at' | 'file_creation' | '-file_creation'
export type AlbumOverview = 'both' | 'desktop' | 'mobile' | 'none'
export type AlbumOverviewStyle = 'rect' | 'circle'

/**
 * Project template (ProfProjectTemplate) — reusable configuration for projects.
 *
 * Naming convention:
 *   project_*  → copied onto the ProfProject
 *   drive_*    → copied onto the project's HeyDrive
 *
 * Semantics: `null` means "not part of the template" and is skipped when applying.
 * Explicitly set values (including '' or false) are applied. Empty `project_tags`
 * are skipped as well (so tags are never cleared by a template).
 *
 * Exception — **clearable** fields (`project_price_list`, `project_contact`, `drive_watermark`, mirroring the
 * backend's `TemplateField.clearable`): there `null` is a meaningful value meaning "nothing
 * set", and applying the template *clears* the attribute instead of skipping it.
 */
export interface ProfProjectTemplate {
  id: string
  name: string
  /** Number of projects currently linked to this template */
  projects_count: number
  created_at: string
  updated_at: string

  // --- Project attributes ---
  /** clearable: `null` = no price list, and applying clears it on the project */
  project_price_list: string | null
  /** On READ: full tag objects. On WRITE: array of tag IDs. */
  project_tags: ProfTag[]
  /** clearable: `null` = "Standardkontakt" — NOT "skip": applying resets the project to the default contact */
  project_contact: string | null

  // --- Drive attributes: design ---
  drive_color_primary: string | null
  drive_color_neutral: string | null
  drive_color_content: string | null
  drive_font: string | null
  drive_font_content: string | null
  drive_default_files_order: FilesOrdering | null
  drive_theme_name: string | null
  drive_spacing: number | null
  drive_layout_mode: LayoutMode | null
  drive_album_overview: AlbumOverview | null
  drive_album_overview_style: AlbumOverviewStyle | null
  drive_are_elements_contained: boolean | null

  // --- Drive attributes: features ---
  drive_is_hide_selection: boolean | null
  drive_is_hide_download: boolean | null
  drive_is_hide_share: boolean | null
  /** clearable: `null` = no watermark, and applying removes it from the drive */
  drive_watermark: string | null
  /** `null` = not part of the template; `''` = explicitly "alle Auflösungen" and applied as such */
  drive_download_options: DownloadOptions | null

  // --- Drive attributes: selection form ---
  drive_is_selection_name_required: boolean | null
  drive_is_selection_email_required: boolean | null
  drive_is_selection_message_required: boolean | null
  drive_opened_image_caption: OpenedImageCaption | null
}

/**
 * Create/update payload. All fields optional; sending `null` removes the
 * attribute from the template (it is then skipped when applying).
 * `project_tags` is an array of tag IDs here (not the objects!).
 */
export interface CreateUpdateProjectTemplateRequest {
  name?: string

  project_price_list?: string | null
  project_tags?: string[]
  /** `null` = "Standardkontakt" (not "remove from template"); omit to leave it unchanged */
  project_contact?: string | null

  drive_color_primary?: string | null
  drive_color_neutral?: string | null
  drive_color_content?: string | null
  drive_font?: string | null
  drive_font_content?: string | null
  drive_default_files_order?: FilesOrdering | null
  drive_theme_name?: string | null
  drive_spacing?: number | null
  drive_layout_mode?: LayoutMode | null
  drive_album_overview?: AlbumOverview | null
  drive_album_overview_style?: AlbumOverviewStyle | null
  drive_are_elements_contained?: boolean | null

  drive_is_hide_selection?: boolean | null
  drive_is_hide_download?: boolean | null
  drive_is_hide_share?: boolean | null
  drive_watermark?: string | null
  drive_download_options?: DownloadOptions | null

  drive_is_selection_name_required?: boolean | null
  drive_is_selection_email_required?: boolean | null
  drive_is_selection_message_required?: boolean | null
  drive_opened_image_caption?: OpenedImageCaption | null
}

/** Create a template from an existing project, or refill an existing one. */
export interface CaptureProjectTemplateRequest {
  /** ID of the project whose project + drive configuration is captured */
  project: string
  /** Optional; when creating, defaults to the project title */
  name?: string
}

/** Response of POST /api/prof/project-templates/{id}/apply/ */
export interface ApplyProjectTemplateResponse {
  /** IDs of the projects the template was applied to */
  applied_to: string[]
}

// --- Comparison project ↔ template -------------------------------------------------

/** How a value has to be normalized before template and project can be compared */
type CompareKind = 'text' | 'color' | 'bool' | 'number' | 'ordering'

/**
 * Mirrors the backend's TEMPLATE_FIELDS: the template field name is the target
 * prefix (`project_` / `drive_`) plus the field name on that model, so each entry
 * maps 1:1 onto `ProfProject` / `HeyDrive`. Tags are handled separately (m2m).
 *
 * `clearable` mirrors the backend flag: for those fields an empty value is meaningful
 * ("really nothing set", cleared on apply) instead of "not part of the template", so
 * they are compared even when the template holds `null`.
 *
 * `compareWhenUnset` is a frontend-only flag with the same effect on the comparison but a
 * different reason — see `diffProjectAgainstTemplate`. It exists for fields a template can
 * still be *unaware of* (added after the template was captured, so the API returns `null`).
 * Without it such a field could never surface, "Vorlage aktualisieren" would never appear,
 * and the template could never learn the field at all.
 */
const DRIVE_TEMPLATE_FIELDS: Array<{ field: keyof HeyDrive, kind: CompareKind, label: string, clearable?: boolean, compareWhenUnset?: boolean }> = [
  { field: 'color_primary', kind: 'color', label: 'Farben' },
  { field: 'color_neutral', kind: 'color', label: 'Farben' },
  { field: 'color_content', kind: 'color', label: 'Farben' },
  { field: 'font', kind: 'text', label: 'Schriften' },
  { field: 'font_content', kind: 'text', label: 'Schriften' },
  { field: 'default_files_order', kind: 'ordering', label: 'Sortierung' },
  { field: 'theme_name', kind: 'text', label: 'Theme' },
  { field: 'spacing', kind: 'number', label: 'Bildabstand' },
  { field: 'layout_mode', kind: 'text', label: 'Layout' },
  { field: 'album_overview', kind: 'text', label: 'Album-Übersicht' },
  { field: 'album_overview_style', kind: 'text', label: 'Album-Übersicht' },
  { field: 'are_elements_contained', kind: 'bool', label: 'Layout' },
  { field: 'is_hide_selection', kind: 'bool', label: 'Auswahl-Funktionen' },
  { field: 'is_hide_download', kind: 'bool', label: 'Auswahl-Funktionen' },
  { field: 'is_hide_share', kind: 'bool', label: 'Auswahl-Funktionen' },
  { field: 'watermark', kind: 'text', label: 'Wasserzeichen', clearable: true },
  { field: 'download_options', kind: 'text', label: 'Download-Optionen', compareWhenUnset: true },
  { field: 'is_selection_name_required', kind: 'bool', label: 'Pflichtfelder bei Auswahl' },
  { field: 'is_selection_email_required', kind: 'bool', label: 'Pflichtfelder bei Auswahl' },
  { field: 'is_selection_message_required', kind: 'bool', label: 'Pflichtfelder bei Auswahl' },
  { field: 'opened_image_caption', kind: 'text', label: 'Bildunterschrift' },
]

function valuesDiffer(templateValue: unknown, currentValue: unknown, kind: CompareKind): boolean {
  switch (kind) {
    case 'bool':
      return !!templateValue !== !!currentValue
    case 'number':
      // null/undefined ("not set") is not the same as 0
      return (templateValue ?? null) !== (currentValue ?? null)
    case 'color':
      return normalizeColor(templateValue) !== normalizeColor(currentValue)
    case 'ordering':
      // The drive may still carry the dashed legacy spelling ("-created-at")
      return normalizeOrdering(templateValue) !== normalizeOrdering(currentValue)
    default:
      return String(templateValue ?? '') !== String(currentValue ?? '')
  }
}

function normalizeColor(value: unknown): string {
  return String(value ?? '').trim().replace(/^#/, '').toLowerCase()
}

function normalizeOrdering(value: unknown): string {
  return String(value ?? '').replace(/([a-z])-([a-z])/g, '$1_$2')
}

/**
 * Which settings of `project` deviate from `template`.
 *
 * `null` template fields are skipped — they are not part of the template. Tags and
 * `compareWhenUnset` fields are the exception: they are compared even when the template
 * has no value, because the result also drives "Vorlage aktualisieren" (project →
 * template), where the project's value is a real difference. The known asymmetry that
 * buys: for those fields "Vorlage anwenden" changes nothing (the backend skips `null`),
 * so they only ever appear alongside a genuine reason to capture. Returns the
 * (deduplicated, German) labels of the deviating groups; an empty array means the
 * project matches its template.
 */
export function diffProjectAgainstTemplate(project: ProfProject, template: ProfProjectTemplate): string[] {
  const labels: string[] = []

  for (const { field, kind, label, clearable, compareWhenUnset } of DRIVE_TEMPLATE_FIELDS) {
    const templateValue = (template as unknown as Record<string, unknown>)[`drive_${field}`]
    // A field the API did not deliver can never be compared.
    if (templateValue === undefined) continue
    // `null` means "not part of the template" — except for clearable fields, where it
    // means "nothing set", and for `compareWhenUnset` fields, where the project's own
    // value is still something "Vorlage aktualisieren" has to be able to capture.
    if (templateValue === null && !clearable && !compareWhenUnset) continue
    if (valuesDiffer(templateValue, project.drive?.[field], kind)) labels.push(label)
  }

  // price_list is clearable too: null is a real value ("keine Preisliste"), not a skip.
  if (template.project_price_list !== undefined
    && valuesDiffer(template.project_price_list, project.price_list, 'text')) {
    labels.push('Preisliste')
  }

  // contact is clearable as well: null = "Standardkontakt", applied as such.
  if (template.project_contact !== undefined
    && valuesDiffer(template.project_contact, project.contact, 'text')) {
    labels.push('Marke')
  }

  // Tags are compared as a set — order is irrelevant, and an empty template tag list still
  // counts as a deviation: applying would not clear the project's tags, but capturing the
  // project into the template very much changes it. Only a field the API did not deliver
  // at all (undefined) is skipped.
  if (Array.isArray(template.project_tags)) {
    const templateTagIds = new Set(template.project_tags.map(t => t.id))
    const projectTagIds = new Set((project.tags ?? []).map(t => t.id))
    if (templateTagIds.size !== projectTagIds.size || [...templateTagIds].some(id => !projectTagIds.has(id))) {
      labels.push('Tags')
    }
  }

  return [...new Set(labels)]
}
